import { authFetch } from "./authApi";

function extractSheetId(input) {
    const v = (input || "").trim();
    if (!v) return "";
    if (!v.includes("/")) return v;

    // Google Sheets URL: .../d/<ID>/...
    const m = v.match(/\/d\/([a-zA-Z0-9-_]+)/);
    return m ? m[1] : v;
}

function extractErrMessage(data, fallback) {
    if (!data) return fallback;
    if (typeof data.detail === "string") return data.detail;
    if (data.detail) {
        try {
            return JSON.stringify(data.detail, null, 2);
        } catch {
            return fallback;
        }
    }
    if (typeof data.message === "string") return data.message;
    return fallback;
}

export async function uploadIracingJson(file) {
    const AUTH_API = process.env.REACT_APP_AUTH_API || "";
    const token = localStorage.getItem("rv_token") || "";
    if (!token) throw new Error("Kein Token gefunden – bitte neu einloggen.");

    const fd = new FormData();
    fd.append("file", file); // Backend erwartet "file"

    const res = await fetch(`${AUTH_API}/api/ligamanager/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(extractErrMessage(data, `HTTP ${res.status}`));
    }
    return data; // { ok, upload_id, filename }
}

export async function runStandingsImport({ sheetInput, tag, upload_id }) {
    const sheet_id = extractSheetId(sheetInput);

    return authFetch("/api/ligamanager/standings-import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            sheet_id,
            tag,
            upload_id,
        }),
    });
}

export async function runStandingsImportSingleClass({ sheetInput, tag, upload_id }) {
    const sheet_id = extractSheetId(sheetInput);

    return authFetch("/api/ligamanager/standings-import-single-class", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            sheet_id,
            tag,
            upload_id,
        }),
    });
}

export async function runResultsImportDb({
    upload_id,
    series_key = "gt3x",
    season_id,
    season_key,
    race_id,
    race_tag,
}) {
    return authFetch("/api/ligamanager/results-import-db", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            upload_id,
            series_key,
            season_id: season_id ? Number(season_id) : null,
            season_key: season_key || null,
            race_id: race_id ? Number(race_id) : null,
            race_tag: race_tag || null,
        }),
    });
}

export async function runRacePointsDb({
    series_key = "gt3x",
    season_id,
    season_key,
    race_id,
    race_tag,
    result_import_id,
}) {
    return authFetch("/api/ligamanager/race-points-db", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            series_key,
            season_id: season_id ? Number(season_id) : null,
            season_key: season_key || null,
            race_id: race_id ? Number(race_id) : null,
            race_tag: race_tag || null,
            result_import_id: result_import_id ? Number(result_import_id) : null,
        }),
    });
}

export async function runStandingsSnapshotDb({
    series_key = "gt3x",
    season_id,
    season_key,
    through_race_id,
    through_race_tag,
}) {
    return authFetch("/api/ligamanager/standings-snapshot-db", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            series_key,
            season_id: season_id ? Number(season_id) : null,
            season_key: season_key || null,
            through_race_id: through_race_id ? Number(through_race_id) : null,
            through_race_tag: through_race_tag || null,
        }),
    });
}

export async function runDriverClassChangeDb({
                                                 series_key = "gt3x",
                                                 season_id,
                                                 season_key,
                                                 season_driver_id,
                                                 new_class,
                                                 new_car_number,
                                             }) {
    return authFetch("/api/ligamanager/class-change-db", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            series_key,
            season_id: season_id ? Number(season_id) : null,
            season_key: season_key || null,
            season_driver_id: Number(season_driver_id),
            new_class: String(new_class || "").trim().toUpperCase(),
            new_car_number: String(new_car_number || "").replace("#", "").trim(),
        }),
    });
}

export async function fetchResultsImportDbRows({
    series_key = "gt3x",
    season_id,
    season_key,
    race_id,
    race_tag,
    result_import_id,
    session_type = "RACE",
}) {
    const params = new URLSearchParams();
    params.set("series_key", series_key);
    if (season_id) params.set("season_id", String(Number(season_id)));
    if (season_key) params.set("season_key", season_key);
    if (race_id) params.set("race_id", String(Number(race_id)));
    if (race_tag) params.set("race_tag", race_tag);
    if (result_import_id) params.set("result_import_id", String(Number(result_import_id)));
    if (session_type) params.set("session_type", session_type);

    return authFetch(`/api/ligamanager/results-import-db/rows?${params.toString()}`, {
        method: "GET",
    });
}

export async function saveResultsImportManualOverrides({
    series_key = "gt3x",
    season_id,
    season_key,
    race_id,
    race_tag,
    result_import_id,
    manual_scope,
    rows = [],
}) {
    return authFetch("/api/ligamanager/results-import-db/manual-overrides", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            series_key,
            season_id: season_id ? Number(season_id) : null,
            season_key: season_key || null,
            race_id: race_id ? Number(race_id) : null,
            race_tag: race_tag || null,
            result_import_id: result_import_id ? Number(result_import_id) : null,
            manual_scope: manual_scope || null,
            rows: Array.isArray(rows) ? rows : [],
        }),
    });
}

export async function runOrgaPenaltyDb({
    series_key = "gt3x",
    season_id,
    season_key,
    race_id,
    race_tag,
    result_import_id,
    penalized_season_driver_id,
    penalty_type = "TIME_PENALTY",
    penalty_seconds = 0,
    penalty_points = 0,
    reason = "",
}) {
    return authFetch("/api/ligamanager/results-import-db/orga-penalty", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            series_key,
            season_id: season_id ? Number(season_id) : null,
            season_key: season_key || null,
            race_id: race_id ? Number(race_id) : null,
            race_tag: race_tag || null,
            result_import_id: result_import_id ? Number(result_import_id) : null,
            penalized_season_driver_id: Number(penalized_season_driver_id),
            penalty_type: penalty_type || "TIME_PENALTY",
            penalty_seconds: Number(penalty_seconds || 0),
            penalty_points: Number(penalty_points || 0),
            reason: String(reason || "").trim() || null,
        }),
    });
}

export function streamJobLogs({ job_id, onLog, onDone, onError }) {
    const AUTH_API = process.env.REACT_APP_AUTH_API || "";
    const token = localStorage.getItem("rv_token") || "";

    if (!token) {
        onError?.(new Error("Kein Token gefunden – bitte neu einloggen."));
        return () => {};
    }

    // Token im Querystring ist unschön, aber EventSource kann keine Custom Headers.
    // => Wir machen SSE über fetch+ReadableStream ODER EventSource mit token=...
    // Ich gebe dir hier die robusteste Variante: fetch streaming (mit Headern)

    const ctrl = new AbortController();

    fetch(`${AUTH_API}/api/jobs/${job_id}/stream`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        signal: ctrl.signal,
    })
        .then(async (res) => {
            if (!res.ok) {
                const txt = await res.text().catch(() => "");
                throw new Error(`SSE HTTP ${res.status}: ${txt || res.statusText}`);
            }

            const reader = res.body.getReader();
            const dec = new TextDecoder("utf-8");
            let buf = "";

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buf += dec.decode(value, { stream: true });

                // SSE Events sind durch \n\n getrennt
                let idx;
                while ((idx = buf.indexOf("\n\n")) >= 0) {
                    const chunk = buf.slice(0, idx);
                    buf = buf.slice(idx + 2);

                    // parse event + data
                    let ev = "message";
                    let data = "";
                    for (const line of chunk.split("\n")) {
                        if (line.startsWith("event:")) ev = line.slice(6).trim();
                        if (line.startsWith("data:")) data += line.slice(5).trim();
                    }

                    if (ev === "log") {
                        try {
                            const obj = JSON.parse(data);
                            onLog?.(obj); // {seq, level, ts, msg}
                        } catch {
                            onLog?.({ level: "INFO", msg: data });
                        }
                    } else if (ev === "done") {
                        onDone?.(data); // done|error|canceled|not_found
                        ctrl.abort();
                        return;
                    }
                }
            }
        })
        .catch((err) => {
            if (String(err).includes("AbortError")) return;
            onError?.(err);
        });

    return () => ctrl.abort();
}

export async function getJob(job_id) {
    return authFetch(`/api/jobs/${job_id}`, { method: "GET" });
}

// Ergänzung für src/lib/ligamanagerApi.js
// Diese Funktionen ans Ende der bestehenden Datei hängen.

function extractRulebookErrMessage(data, fallback) {
    if (!data) return fallback;
    if (typeof data.detail === "string") return data.detail;
    if (data.detail) {
        try {
            return JSON.stringify(data.detail, null, 2);
        } catch {
            return fallback;
        }
    }
    if (typeof data.message === "string") return data.message;
    return fallback;
}

export async function fetchPublicRulebook(slug = "gt3x") {
    const AUTH_API = process.env.REACT_APP_AUTH_API || "";
    const params = new URLSearchParams();
    params.set("slug", slug || "gt3x");

    const res = await fetch(`${AUTH_API}/api/public/rulebook/current?${params.toString()}`, {
        method: "GET",
        headers: { Accept: "application/json" },
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(extractRulebookErrMessage(data, `HTTP ${res.status}`));
    }

    return data;
}

export async function fetchAdminRulebookStatus(slug = "gt3x") {
    const params = new URLSearchParams();
    params.set("slug", slug || "gt3x");

    return authFetch(`/api/manager/admin/rulebook/status?${params.toString()}`, {
        method: "GET",
    });
}

export async function syncAdminRulebook({
                                            slug = "gt3x",
                                            title = "GT3 Xperience Regelwerk",
                                            google_doc_id,
                                            google_doc_url,
                                            force = false,
                                        } = {}) {
    return authFetch("/api/manager/admin/rulebook/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            slug,
            title,
            google_doc_id: google_doc_id || null,
            google_doc_url: google_doc_url || null,
            force: Boolean(force),
        }),
    });
}
